<template>
  <v-dialog :value="value" persistent max-width="900" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">visibility</v-icon>
        Anlaşma Detayı
        <v-spacer />
        <v-btn icon dark @click="close"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-card-text class="pa-4">
        <div v-if="loading" class="text-xs-center py-4">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="data">
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Firma / Yetkili</div>
              <div class="subtitle-1 font-weight-bold">
                {{ data.companyName }} — {{ data.contactPerson }}
              </div>
            </v-flex>
            <v-flex xs12 sm6 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Durum / Ödeme Günü</div>
              <div class="subtitle-1 font-weight-bold">
                <v-chip small :color="agreementChipColor(data.status)" text-color="white">
                  {{ data.statusName }}
                </v-chip>
                · {{ formatDateWithDay(data.paymentDate) }}
              </div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Anlaşma Tarihi</div>
              <div class="body-2">{{ formatDateTime(data.agreementDate) }}</div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Sipariş Sayısı</div>
              <div class="body-2 font-weight-bold">{{ data.orderCount }}</div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Gram Fiyatı</div>
              <div class="body-2 font-weight-bold">{{ formatGramPrice(data.goldGramPrice) }} ₺/gr</div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Sip. Has Gram</div>
              <div class="body-2 font-weight-bold">{{ formatGram(data.totalPureGram) }} gr</div>
            </v-flex>
            <v-flex xs12 sm3 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Alınacak Has Altın</div>
              <div class="body-2 blue--text font-weight-bold">{{ formatGram(acquiredGram) }} gr</div>
              <div v-if="acquiredDelta !== 0" class="caption" :class="acquiredDelta > 0 ? 'green--text' : 'red--text'">
                {{ acquiredDelta > 0 ? 'Kar' : 'Zarar' }}: {{ acquiredDelta > 0 ? '+' : '' }}{{ formatGram(acquiredDelta) }} gr
              </div>
            </v-flex>
            <v-flex xs12 sm6 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Fatura Toplamı</div>
              <div class="body-2">{{ formatCurrency(data.totalInvoiceAmount) }}</div>
            </v-flex>
            <v-flex xs12 sm6 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Net Hesaba Yatan (Sistem)</div>
              <div class="body-2">{{ formatCurrency(data.totalNetCash) }}</div>
            </v-flex>
            <v-flex xs12 sm6 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">
                Anlaşılan Tutar (Darphaneye Ödenecek)
                <span v-if="agreedExtra !== 0" :class="agreedExtra > 0 ? 'green--text font-weight-bold' : 'red--text font-weight-bold'">
                  · {{ agreedExtra > 0 ? 'Ekstra' : 'Eksik' }}: {{ agreedExtra > 0 ? '+' : '' }}{{ formatCurrency(agreedExtra) }}
                </span>
              </div>
              <div class="subtitle-1 blue--text font-weight-bold">
                {{ formatCurrency(data.agreedAmount) }}
              </div>
            </v-flex>
            <v-flex v-if="data.paidAt" xs12 sm6 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Ödendi</div>
              <div class="body-2">{{ formatDateTime(data.paidAt) }}</div>
            </v-flex>
            <v-flex v-if="data.notes" xs12 class="px-2 mb-2">
              <div class="caption grey--text text--darken-1">Notlar</div>
              <div class="body-2">{{ data.notes }}</div>
            </v-flex>
          </v-layout>

          <v-divider class="mb-3" />

          <div class="subtitle-1 font-weight-bold mb-2">Kapsanan Siparişler</div>
          <v-data-table
            :headers="orderHeaders"
            :items="data.orderDetails || []"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ props.item.orderNumber }}</td>
                <td>
                  <span v-if="props.item.mpName" class="inv-chip" :class="mpChipClass(props.item.mpId)">{{ props.item.mpName }}</span>
                  <div v-if="props.item.ownerCompanyName" class="inv-meta">{{ props.item.ownerCompanyName }}</div>
                </td>
                <td class="text-no-wrap">{{ formatDate(props.item.completedDate) }}</td>
                <td>{{ props.item.customerName }}</td>
                <td>{{ props.item.shippingCity }}</td>
                <td class="text-xs-right">{{ formatCurrency(props.item.invoiceAmount) }}</td>
                <td class="text-xs-right">{{ formatGram(props.item.totalPureGram) }} gr</td>
              </tr>
            </template>
            <template v-slot:no-data>
              <v-alert :value="true" color="info" icon="info">
                Bu anlaşmada sipariş bulunamadı.
              </v-alert>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn flat @click="close">Kapat</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'MintAgreementDetailDialog',
  props: {
    value: { type: Boolean, default: false },
    agreementId: { type: Number, default: null },
  },
  data() {
    return {
      data: null,
      loading: false,
      orderHeaders: [
        { text: 'Sipariş No', value: 'orderNumber', sortable: false },
        { text: 'Pazaryeri', value: 'mpName', sortable: false },
        { text: 'Tamamlanma', value: 'completedDate', sortable: false },
        { text: 'Müşteri', value: 'customerName', sortable: false },
        { text: 'Şehir', value: 'shippingCity', sortable: false },
        { text: 'Fatura', value: 'invoiceAmount', sortable: false, align: 'right' },
        { text: 'Has Gram', value: 'totalPureGram', sortable: false, align: 'right' },
      ],
    };
  },
  computed: {
    agreedExtra() {
      if (!this.data) return 0;
      const agreed = Number(this.data.agreedAmount) || 0;
      const net = Number(this.data.totalNetCash) || 0;
      return agreed - net;
    },
    acquiredGram() {
      if (!this.data) return 0;
      const agreed = Number(this.data.agreedAmount) || 0;
      const price = Number(this.data.goldGramPrice) || 0;
      if (!agreed || !price) return 0;
      return agreed / price;
    },
    acquiredDelta() {
      const acquired = this.acquiredGram;
      const sipGram = Number((this.data && this.data.totalPureGram)) || 0;
      if (!acquired || !sipGram) return 0;
      return acquired - sipGram;
    },
  },
  watch: {
    value(newVal) {
      if (newVal && this.agreementId) {
        this.fetch();
      } else if (!newVal) {
        this.data = null;
      }
    },
    agreementId(newId) {
      if (this.value && newId) {
        this.fetch();
      }
    },
  },
  methods: {
    mpChipClass(mpId) {
      if (mpId === 1) return 'inv-chip--brown';
      if (mpId === 3) return 'inv-chip--purple';
      if (mpId === 4) return 'inv-chip--blue';
      if (mpId === 5) return 'inv-chip--teal';
      if (mpId === 6) return 'inv-chip--orange';
      return 'inv-chip--grey';
    },
    async fetch() {
      this.data = null;
      this.loading = true;
      try {
        const full = await this.$store.dispatch('mintAgreements/get', this.agreementId);
        this.data = full;
      } catch (e) {
        this.$emit('error', e);
        this.close();
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit('input', false);
    },
    formatDate(v) {
      if (!v) return '—';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('tr-TR');
    },
    formatDateWithDay(v) {
      if (!v) return '—';
      const d = new Date(v);
      const dayName = d.toLocaleDateString('tr-TR', { weekday: 'short' });
      return `${d.toLocaleDateString('tr-TR')} ${dayName}`;
    },
    formatDateTime(v) {
      if (!v) return '—';
      const d = new Date(v);
      return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
    },
    formatCurrency(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n);
    },
    formatGram(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(n);
    },
    formatGramPrice(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    },
    agreementChipColor(status) {
      if (status === 0) return 'orange';
      if (status === 1) return 'green';
      return 'grey';
    },
  },
};
</script>
